import { Injectable, inject } from '@angular/core';
import { Map } from 'maplibre-gl';
import { WindElement } from 'src/app/models/wind';
import { SettingsService, MapSkin } from 'src/app/services/account/settings.service';
import { UtilsService } from 'src/app/services/utils/utils.service';

interface WindParticle {
  lng: number;
  lat: number;
  age: number;
  maxAge: number;
  prevX: number;
  prevY: number;
}

@Injectable({
  providedIn: 'root'
})
export class WindService {

  private mapInstance: Map | null = null;
  private overlayCanvas: HTMLCanvasElement | null = null;
  private overlayCtx: CanvasRenderingContext2D | null = null;
  private windData: WindElement[] = [];
  private windAnimationId: number | null = null;
  private windParticles: WindParticle[] = [];
  private windBounds = { minLng: 0, maxLng: 0, minLat: 0, maxLat: 0 };
  private windLookupMap: { [key: string]: WindElement } = {};
  private windCellSize = 0.5;
  private readonly PARTICLE_COUNT_MIN = 400;
  private readonly PARTICLE_COUNT_MAX = 1000;
  private readonly TARGET_FPS = 15;
  private readonly FADE_ALPHA = 0.06;
  private lastFrameTime = 0;
  private mapMoved = false;
  private framesSinceClear = 0;
  private onMoveHandler: (() => void) | null = null;
  private onResizeHandler: (() => void) | null = null;

  private readonly settingsService = inject(SettingsService);
  private readonly utilsService = inject(UtilsService);

  public initWind(mapInstance: Map, windElements: WindElement[]): void {
    return;
    /*this.clear();
    if (!mapInstance || windElements.length === 0) return;
    this.mapInstance = mapInstance;
    this.windData = windElements;
    this.buildWindLookup();
    this.initWindParticles();
    this.setupOverlayCanvas();
    this.startWindAnimation();

    this.onMoveHandler = () => { this.mapMoved = true; };
    this.mapInstance.on('move', this.onMoveHandler);

    this.onResizeHandler = () => this.resizeOverlay();
    this.mapInstance.on('resize', this.onResizeHandler);*/
  }

  public clear(): void {
    if (this.windAnimationId !== null) {
      cancelAnimationFrame(this.windAnimationId);
      this.windAnimationId = null;
    }
    if (this.mapInstance && this.onMoveHandler) {
      this.mapInstance.off('move', this.onMoveHandler);
      this.onMoveHandler = null;
    }
    if (this.mapInstance && this.onResizeHandler) {
      this.mapInstance.off('resize', this.onResizeHandler);
      this.onResizeHandler = null;
    }
    if (this.overlayCanvas?.parentNode) {
      this.overlayCanvas.parentNode.removeChild(this.overlayCanvas);
    }
    this.overlayCanvas = null;
    this.overlayCtx = null;
    this.windData = [];
    this.windParticles = [];
    this.windLookupMap = {};
    this.mapMoved = false;
    this.framesSinceClear = 0;
  }

  private setupOverlayCanvas(): void {
    if (!this.mapInstance) return;
    const mapCanvas = this.mapInstance.getCanvas();
    const container = this.mapInstance.getCanvasContainer();

    this.overlayCanvas = document.createElement('canvas');
    this.overlayCanvas.style.position = 'absolute';
    this.overlayCanvas.style.top = '0';
    this.overlayCanvas.style.left = '0';
    this.overlayCanvas.style.width = '100%';
    this.overlayCanvas.style.height = '100%';
    this.overlayCanvas.style.pointerEvents = 'none';
    // CSS pixel resolution — no need for device pixel ratio
    this.overlayCanvas.width = mapCanvas.clientWidth;
    this.overlayCanvas.height = mapCanvas.clientHeight;

    container.appendChild(this.overlayCanvas);
    this.overlayCtx = this.overlayCanvas.getContext('2d');
  }

  private resizeOverlay(): void {
    if (!this.overlayCanvas || !this.mapInstance) return;
    const mapCanvas = this.mapInstance.getCanvas();
    this.overlayCanvas.width = mapCanvas.clientWidth;
    this.overlayCanvas.height = mapCanvas.clientHeight;
  }

  private buildWindLookup(): void {
    this.windLookupMap = {};
    if (this.windData.length < 2) return;

    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    for (const w of this.windData) {
      if (w.Location.lng < minLng) minLng = w.Location.lng;
      if (w.Location.lng > maxLng) maxLng = w.Location.lng;
      if (w.Location.lat < minLat) minLat = w.Location.lat;
      if (w.Location.lat > maxLat) maxLat = w.Location.lat;
    }
    this.windBounds = { minLng, maxLng, minLat, maxLat };

    let colSize = 0;
    const firstLng = this.windData[0].Location.lng;
    for (let i = 1; i < this.windData.length; i++) {
      if (this.windData[i].Location.lng !== firstLng) {
        colSize = i;
        break;
      }
    }
    if (colSize > 1) {
      this.windCellSize = Math.abs(this.windData[1].Location.lat - this.windData[0].Location.lat) || 0.5;
    }

    for (const w of this.windData) {
      const key = `${Math.round(w.Location.lng / this.windCellSize)},${Math.round(w.Location.lat / this.windCellSize)}`;
      this.windLookupMap[key] = w;
    }
  }

  private getWindAt(lng: number, lat: number): WindElement | null {
    const key = `${Math.round(lng / this.windCellSize)},${Math.round(lat / this.windCellSize)}`;
    return this.windLookupMap[key] || null;
  }

  private initWindParticles(): void {
    const count = Math.min(Math.max(this.windData.length, this.PARTICLE_COUNT_MIN), this.PARTICLE_COUNT_MAX);
    this.windParticles = [];
    for (let i = 0; i < count; i++) {
      this.windParticles.push(this.createWindParticle(true));
    }
  }

  private createWindParticle(randomAge: boolean): WindParticle {
    const b = this.windBounds;
    return {
      lng: b.minLng + Math.random() * (b.maxLng - b.minLng),
      lat: b.minLat + Math.random() * (b.maxLat - b.minLat),
      age: randomAge ? Math.floor(Math.random() * 120) : 0,
      maxAge: 80 + Math.floor(Math.random() * 60),
      prevX: -1,
      prevY: -1
    };
  }

  private startWindAnimation(): void {
    const frameInterval = 1000 / this.TARGET_FPS;
    const animate = (timestamp: number) => {
      this.windAnimationId = requestAnimationFrame(animate);
      const elapsed = timestamp - this.lastFrameTime;
      if (elapsed < frameInterval) return;
      this.lastFrameTime = timestamp - (elapsed % frameInterval);
      this.drawWindFrame();
    };
    this.windAnimationId = requestAnimationFrame(animate);
  }

  private drawWindFrame(): void {
    if (!this.overlayCanvas || !this.overlayCtx || !this.mapInstance || this.windData.length === 0) return;

    const ctx = this.overlayCtx;
    const w = this.overlayCanvas.width;
    const h = this.overlayCanvas.height;

    // On map move: clear canvas and invalidate previous positions
    if (this.mapMoved) {
      this.mapMoved = false;
      ctx.clearRect(0, 0, w, h);
      for (const p of this.windParticles) {
        p.prevX = -1;
      }
      this.framesSinceClear = 0;
    }

    // Periodic full clear to prevent alpha-dust build-up from 8-bit rounding
    this.framesSinceClear++;
    if (this.framesSinceClear > 300) {
      this.framesSinceClear = 0;
      ctx.clearRect(0, 0, w, h);
      for (const p of this.windParticles) {
        p.prevX = -1;
      }
    }

    // Fade existing trails — this creates the tail effect
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = `rgba(0,0,0,${this.FADE_ALPHA})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';

    const skin = this.settingsService.getMap().Skin;
    const isLight = skin === MapSkin.Light;
    const zoom = this.mapInstance.getZoom();
    const speedFactor = 0.0004 * Math.pow(2, 7 - zoom);

    // Single batched stroke for all particles
    ctx.strokeStyle = isLight ? 'rgba(60,60,60,0.8)' : 'rgba(220,220,220,0.8)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();

    for (let i = 0; i < this.windParticles.length; i++) {
      const p = this.windParticles[i];
      p.age++;

      if (p.age > p.maxAge) {
        this.windParticles[i] = this.createWindParticle(false);
        continue;
      }

      const wind = this.getWindAt(p.lng, p.lat);
      if (!wind || wind.Speed === 0 || wind.Opacity === 0) {
        this.windParticles[i] = this.createWindParticle(false);
        continue;
      }

      const dir = this.utilsService.toRad(wind.Angle);
      const speed = wind.Speed * speedFactor;
      p.lng -= Math.sin(dir) * speed;
      p.lat -= Math.cos(dir) * speed;

      const pt = this.mapInstance.project([p.lng, p.lat]);

      if (pt.x < -10 || pt.x > w + 10 || pt.y < -10 || pt.y > h + 10) {
        this.windParticles[i] = this.createWindParticle(false);
        continue;
      }

      if (p.prevX >= 0) {
        ctx.moveTo(p.prevX, p.prevY);
        ctx.lineTo(pt.x, pt.y);
      }
      p.prevX = pt.x;
      p.prevY = pt.y;
    }

    ctx.stroke();
  }
}
